import React, { useEffect, useState } from 'react';
import './ModalitySelectionStep.css';
import { BookingModality, Modality, Turn } from '../../models/types';
import { fetchModalities } from '../../services/modalities';
import authenticate from '../../utils/authenticate';
import { getGeneralModalityImageUrl, getModalityImageUrl } from '../../utils/modalityImages';
import { toIncludeItems } from '../../utils/modalityIncludes';
import { ModalityHeaderIcon, ModalityIncludeIcon } from './ModalityIcons';

interface ModalitySelectionStepProps {
    shopId: number;
    shopName?: string;
    roomNum: number;
    selectedDate: Date | null;
    selectedTurn: Turn | null;
    selectedModality: BookingModality | null;
    onModalitySelect: (modality: Modality) => void;
}

const currencyFormatter = new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});

const ModalityImage: React.FC<{
    modality: Modality;
    shopId: number;
    shopName?: string;
}> = ({ modality, shopId, shopName }) => {
    const primaryUrl =
        modality.imageUrl ?? getModalityImageUrl(modality.id, shopId, shopName);
    const generalFallbackUrl = getGeneralModalityImageUrl(modality.id);
    const placeholderUrl = `https://via.placeholder.com/400x200/007bff/ffffff?text=${encodeURIComponent(modality.label)}`;
    const [src, setSrc] = useState(primaryUrl);

    return (
        <img
            src={src}
            alt={modality.label}
            className="modality-image"
            onError={() => {
                if (src === primaryUrl && primaryUrl !== generalFallbackUrl) {
                    setSrc(generalFallbackUrl);
                } else if (src !== placeholderUrl) {
                    setSrc(placeholderUrl);
                }
            }}
        />
    );
};

const ModalitySelectionStep: React.FC<ModalitySelectionStepProps> = ({
    shopId,
    shopName,
    roomNum,
    selectedDate,
    selectedTurn,
    selectedModality,
    onModalitySelect,
}) => {
    const [modalities, setModalities] = useState<Modality[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadModalities = async () => {
            if (!selectedDate || !selectedTurn) {
                setModalities([]);
                setLoading(false);
                return;
            }

            setLoading(true);

            const token = await authenticate();
            if (!token) {
                alert('Failed to authenticate');
                setLoading(false);
                return;
            }

            const year = String(selectedDate.getFullYear());
            const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
            const day = String(selectedDate.getDate()).padStart(2, '0');

            try {
                const data = await fetchModalities({
                    shopId,
                    shopName,
                    turnId: selectedTurn.turn_id,
                    year,
                    month,
                    day,
                    token,
                });
                setModalities(data);
            } catch (error) {
                console.error('Error loading modalities:', error);
            } finally {
                setLoading(false);
            }
        };

        loadModalities();
    }, [shopId, shopName, roomNum, selectedDate, selectedTurn]);

    const formatPrice = (price: number | null) => {
        if (price === null) return 'Consultar precio';
        return currencyFormatter.format(price);
    };

    if (loading) {
        return (
            <div className="modality-selection-step">
                <div className="modality-loading">Cargando modalidades...</div>
            </div>
        );
    }

    return (
        <div className="modality-selection-step">
            <div className="modalities-grid">
                {modalities.map((modality) => {
                    const includeItems = toIncludeItems(modality.includes);
                    const isSelected = selectedModality === modality.id;

                    return (
                        <button
                            type="button"
                            key={modality.id}
                            className={`modality-card modality-card--${modality.id}${isSelected ? ' selected' : ''}`}
                            onClick={() => onModalitySelect(modality)}
                            aria-pressed={isSelected}
                        >
                            <div className="modality-card-header">
                                <span className="modality-header-icon" aria-hidden="true">
                                    <ModalityHeaderIcon modalityId={modality.id} />
                                </span>
                                <div className="modality-header-text">
                                    <span className="modality-name">{modality.label}</span>
                                    <span className="modality-tagline">{modality.subtitle}</span>
                                </div>
                            </div>

                            <div className="modality-image-container">
                                <ModalityImage modality={modality} shopId={shopId} shopName={shopName} />
                            </div>

                            <div className="modality-card-body">
                                <h3 className="modality-headline">{modality.headline}</h3>
                                <p className="modality-description">{modality.description}</p>

                                <div className="modality-includes">
                                    <span className="modality-includes-title">Incluye</span>
                                    <ul
                                        className={`modality-includes-list${includeItems.length === 1 ? ' modality-includes-list--single' : ''}`}
                                    >
                                        {includeItems.map((item) => (
                                            <li key={`${modality.id}-${item.label}`} className="modality-include-item">
                                                <span className="modality-include-icon" aria-hidden="true">
                                                    <ModalityIncludeIcon kind={item.kind} />
                                                </span>
                                                <span className="modality-include-label">{item.label}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>

                            <div className="modality-price">
                                {modality.price !== null ? (
                                    <>
                                        <span className="modality-price-prefix">Desde</span>
                                        <span className="modality-price-value">{formatPrice(modality.price)}</span>
                                    </>
                                ) : (
                                    <span className="modality-price-value">{formatPrice(null)}</span>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default ModalitySelectionStep;
